import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, Palette, PlugZap, UserCog } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_FAMILY_GROUP, ADMIN_GROUP, NAV_GROUPS, canSee } from "@/lib/nav";
import { HouseQ } from "@/components/brand/Brand";
import { KidAvatar, AdultAvatar } from "@/components/Avatars";
import NavGroup from "./NavGroup";
import NavGroupHeader from "./NavGroupHeader";
import NavItem from "./NavItem";
import HubSwitcher from "./HubSwitcher";

const ROLE = { owner: "Owner", coparent: "Co-parent", relative: "Relative", kid: "Kid", admin: "Admin · demo hub", support: "Support view" };
const PLATFORM = { super_admin: "Super admin", admin: "Admin" };

export default function Sidebar({ hub, onNavigate }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { session, signOut } = useAuth();
  const [hoverId, setHoverId] = useState(null);
  const base = hub ? `/h/${hub.id}/` : "/";
  const rel = loc.pathname.startsWith(base) ? loc.pathname.slice(base.length) : "";
  const isKid = session?.type === "kid";
  const platformRole = session?.user?.platform_role;
  const groups = hub
    ? NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => (i.ownerOnly ? hub.my_role === "owner" && hub.view_mode === "member" : i.giftOnly ? !hub.is_demo && hub.view_mode === "member" && !["owner", "kid"].includes(hub.my_role) : i.everyone || canSee(i.section ?? g.section, hub))) })).filter((g) => g.items.length)
    : [ADMIN_FAMILY_GROUP];
  if (platformRole) groups.push({ ...ADMIN_GROUP, items: ADMIN_GROUP.items.filter((i) => !i.superOnly || platformRole === "super_admin") });
  const myLookPath = hub ? `${base}family/my-look` : "/admin/my-look";
  const hrefOf = (item) => (item.adminOnly ? `/${item.path}` : base + item.path);
  const isActive = (item) => {
    const r = item.adminOnly ? loc.pathname.slice(1) : rel;
    return r === item.path || r.startsWith(`${item.path}/`);
  };
  // Accordion: one group open at a time; the group holding the current page opens on navigation, tapping its header closes it.
  const activeGroup = groups.find((g) => g.items.some(isActive))?.label || null;
  const [openGroup, setOpenGroup] = useState(activeGroup);
  useEffect(() => { setOpenGroup(activeGroup); }, [activeGroup]);
  const toggleGroup = (label) => setOpenGroup((cur) => (cur === label ? null : label));
  const go = (item) => {
    nav(hrefOf(item));
    onNavigate?.();
  };

  const out = async () => {
    await signOut();
    nav(isKid ? "/kid" : "/signin", { replace: true });
  };

  return (
    <>
      <div className="flex items-center gap-2.5 px-4 pb-3 pt-5">
        <HouseQ size={30} />
        <div className="min-w-0 leading-none">
          <div className="font-display text-[15px] font-extrabold tracking-[-0.01em] text-q-wordmark">The Queue</div>
          <div className="mt-1 text-[8.5px] font-bold uppercase tracking-[0.32em] text-q-faint">Home Hub</div>
        </div>
      </div>
      {hub && (
        <div className="px-3 pb-3">
          <HubSwitcher hub={hub} onNavigate={onNavigate} />
        </div>
      )}
      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 pb-4" data-testid="sidebar-nav">
        {groups.map((g) => {
          const items = g.items.map((i) => ({ ...i, glowRgb: g.glowRgb }));
          if (g.solo || (g.label === "Home" && items.length === 1)) {
            const solo = items[0];
            return (
              <div key={g.label} className="mb-0.5">
                <NavGroupHeader group={g} open={isActive(solo)} active={isActive(solo)} onToggle={() => go(solo)} solo />
              </div>
            );
          }
          return (
            <NavGroup key={g.label} group={{ ...g, items }} open={openGroup === g.label} active={items.some(isActive)} onToggle={() => toggleGroup(g.label)} flyout={!onNavigate}>
              {items.map((item) => (
                <NavItem
                  key={item.id}
                  hub={item}
                  isActive={isActive(item)}
                  isHovered={hoverId === item.id}
                  isAdmin={!!platformRole}
                  count={item.countKey ? hub?.counts?.[item.countKey] || 0 : 0}
                  onClick={() => go(item)}
                  onMouseEnter={() => setHoverId(item.id)}
                  onMouseLeave={() => setHoverId(null)}
                />
              ))}
            </NavGroup>
          );
        })}
      </nav>
      <div className="flex items-center gap-2.5 border-t border-q-border px-3 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 -m-1 text-left outline-none transition-colors hover:bg-q-raised" data-testid="sidebar-user-menu-btn">
            {isKid ? <KidAvatar avatar={session.kid.avatar} size={32} /> : <AdultAvatar name={session?.user?.name || ""} picture={session?.user?.picture} size={32} />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold text-q-text" data-testid="sidebar-user-name">{isKid ? session.kid.name : session?.user?.name}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-q-faint" data-testid="sidebar-user-role">
                {hub ? ROLE[hub.my_role] : PLATFORM[platformRole]}
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="q-chrome w-52 border-q-line bg-q-surface" data-testid="user-menu">
            <DropdownMenuItem onSelect={() => { nav(myLookPath); onNavigate?.(); }} data-testid="user-menu-my-look"><Palette className="mr-2 h-4 w-4" /> My Look</DropdownMenuItem>
            {hub && <DropdownMenuItem onSelect={() => { nav(`${base}account/notifications`); onNavigate?.(); }} data-testid="user-menu-notifications"><Bell className="mr-2 h-4 w-4" /> My account · Notifications</DropdownMenuItem>}
            {hub && session?.type === "adult" && <DropdownMenuItem onSelect={() => { nav(`${base}account/connected-ai`); onNavigate?.(); }} data-testid="user-menu-connected-ai"><PlugZap className="mr-2 h-4 w-4" /> My account · Connected AI</DropdownMenuItem>}
            {hub && session?.type === "adult" && <DropdownMenuItem onSelect={() => { nav(`${base}account/settings`); onNavigate?.(); }} data-testid="user-menu-account"><UserCog className="mr-2 h-4 w-4" /> Settings · Account</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={out} data-testid="user-menu-signout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={out} className="q-btn q-btn-ghost px-2 py-2" title="Sign out" data-testid="sidebar-signout-btn" aria-label="Sign out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </>
  );
}
